// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Analysts.js
// All analyst price targets, sortable by any column. Sorting by a per-ticker
// aggregate column stacks that ticker's rows together.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import Navbar from '../components/Navbar';

const S = {
  page:    { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  wrap:    { flex: 1, overflowY: 'auto', padding: '24px 20px' },
  inner:   { width: '100%' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  input:   { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 10px', fontSize: 13, color: 'var(--text)', outline: 'none', width: 260 },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:      { position: 'sticky', top: 0, zIndex: 5, background: 'var(--surface-2)', borderBottom: '2px solid var(--border)', padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  td:      { padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'nowrap' },
  msg:     { padding: 40, textAlign: 'center', color: 'var(--text-muted)' },
  pgBar:      { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 8 },
  pageBtn:    { padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface-2)', color: 'var(--text)', fontSize: 12, cursor: 'pointer', minWidth: 30 },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 12, flexWrap: 'wrap' },
};

const COLS = [
  { key: 'ticker',   label: 'Ticker',   numeric: false },
  { key: 'name',     label: 'Name',     numeric: false },
  { key: 'sector',   label: 'Sector',   numeric: false },
  { key: 'industry', label: 'Industry', numeric: false },
  { key: 'date',    label: 'Date',       numeric: false },
  { key: 'analyst', label: 'Analyst',    numeric: false },
  { key: 'firm',    label: 'Firm',       numeric: false },
  { key: 'rating',  label: 'Rating',     numeric: false },
  { key: 'target',  label: 'Target',     numeric: true  },
  { key: 'close',   label: 'Last',       numeric: true  },
  { key: 'pct',     label: '% vs Last',  numeric: true  },
  { key: 'avg12',   label: 'Avg 12m %',  numeric: true  },
  { key: 'high6',   label: 'High 6m %',  numeric: true  },
  { key: 'high12',  label: 'High 12m %', numeric: true  },
  { key: 'high18',  label: 'High 18m %', numeric: true  },
];

const fmtPct   = v => v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(1) + '%';
const pctColor = v => v == null ? 'var(--text-muted)' : v >= 0 ? 'var(--up)' : 'var(--down)';
const clsColor = c => c === 'buy' ? 'var(--success)' : c === 'sell' ? 'var(--error)' : 'var(--text-muted)';

export function AnalystTable({ rows, cols, defaultSort }) {
  const [sort, setSort]           = useState(defaultSort);
  const [page, setPage]           = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const wrapRef = useRef(null);
  const topRef  = useRef(null);
  const [scrollW, setScrollW] = useState(0);

  useEffect(() => setPage(1), [rows, sort, rowsPerPage]);

  const sorted = useMemo(() => {
    const col = cols.find(c => c.key === sort.key);
    return rows.slice().sort((a, b) => {
      let x = a[sort.key], y = b[sort.key];
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (col.numeric) { x = +x; y = +y; }
      else { x = String(x).toLowerCase(); y = String(y).toLowerCase(); }
      return sort.dir === 'asc' ? (x < y ? -1 : x > y ? 1 : 0) : (x > y ? -1 : x < y ? 1 : 0);
    });
  }, [rows, cols, sort]);

  const onSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });

  // Cross-highlighting: sorted by % vs Last → tint by rating age; sorted by
  // date → tint by % vs Last magnitude.
  const rowTint = r => {
    if (sort.key === 'pct' && r.date) {
      const days = (Date.now() - new Date(r.date)) / 864e5;
      if (days <= 183) return 'var(--accent-dim)';
      if (days <= 365) return 'var(--accent-faint)';
    }
    if (sort.key === 'date' && r.pct != null) {
      if (r.pct >= 500) return 'var(--accent-dim)';
      if (r.pct >= 100) return 'var(--accent-faint)';
    }
    return undefined;
  };

  const cell = (r, key) => {
    switch (key) {
      case 'ticker':
        return <span style={{ color: 'var(--accent)', fontWeight: 700, cursor: 'pointer' }}
          onClick={() => window.open('/chart?t=' + encodeURIComponent(r.ticker), '_blank')}>{r.ticker}</span>;
      case 'analyst':
        return <Link to={'/analysts/analyst/' + encodeURIComponent(r.analyst)} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{r.analyst}</Link>;
      case 'rating':
        return <span style={{ fontWeight: 600, color: clsColor(r.cls) }}>{r.rating || '—'}</span>;
      case 'target':
        return r.target == null ? '—' : `${r.target.toFixed(2)} ${r.currency || ''}`;
      case 'close':
        return r.close == null ? '—' : r.close.toFixed(2);
      case 'pct': case 'avg12': case 'high6': case 'high12': case 'high18':
        return <span style={{ fontWeight: 600, color: pctColor(r[key]) }}>{fmtPct(r[key])}</span>;
      case 'firm':
        return <span style={{ color: 'var(--text-muted)' }}>{r.firm || '—'}</span>;
      default:
        return r[key] ?? '—';
    }
  };

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const pageData   = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => {
    if (wrapRef.current) setScrollW(wrapRef.current.scrollWidth);
  }, [pageData]);

  const sync = (from, to) => {
    if (from.current && to.current && to.current.scrollLeft !== from.current.scrollLeft)
      to.current.scrollLeft = from.current.scrollLeft;
  };

  function pageButtons() {
    const btns = [], delta = 2;
    const left = Math.max(2, page - delta), right = Math.min(totalPages - 1, page + delta);
    btns.push(1);
    if (left > 2) btns.push('...');
    for (let i = left; i <= right; i++) btns.push(i);
    if (right < totalPages - 1) btns.push('...');
    if (totalPages > 1) btns.push(totalPages);
    return btns;
  }

  return (
    <div>
      {totalPages > 1 && (
        <div style={S.pgBar}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Rows: <input type="number" min="10" max="500" value={rowsPerPage}
              onChange={e => setRowsPerPage(Math.max(10, parseInt(e.target.value) || 50))}
              style={{ ...S.input, width: 60, padding: '3px 6px' }} />
          </label>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
        </div>
      )}
      <div ref={topRef} onScroll={() => sync(topRef, wrapRef)}
           style={{ overflowX: 'auto', overflowY: 'hidden', marginBottom: 2 }}>
        <div style={{ width: scrollW, height: 1 }} />
      </div>
      <div ref={wrapRef} onScroll={() => sync(wrapRef, topRef)} style={{ overflowX: 'auto' }}>
      <table style={S.table}>
      <thead>
        <tr>
          {cols.map(c => (
            <th key={c.key} style={{ ...S.th, textAlign: c.numeric ? 'right' : 'left' }} onClick={() => onSort(c.key)}>
              {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {pageData.map((r, i) => (
          <tr key={i} style={{ background: rowTint(r) }}>
            {cols.map(c => (
              <td key={c.key} style={{ ...S.td, textAlign: c.numeric ? 'right' : 'left', fontVariantNumeric: 'tabular-nums' }}>
                {cell(r, c.key)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      </table>
      </div>
      {totalPages > 1 && (
        <div style={S.pagination}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={S.pageBtn}>«</button>
          <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={S.pageBtn}>‹</button>
          {pageButtons().map((p, i) => p === '...'
            ? <span key={`e${i}`} style={{ color: 'var(--text-muted)', padding: '0 4px' }}>...</span>
            : <button key={p} onClick={() => setPage(p)} style={{ ...S.pageBtn, ...(page === p ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--on-accent)' } : {}) }}>{p}</button>
          )}
          <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={S.pageBtn}>›</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={S.pageBtn}>»</button>
        </div>
      )}
    </div>
  );
}

export default function Analysts() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [params] = useSearchParams();
  const [q, setQ]             = useState(params.get('q') || '');   // ?q= pre-fills the filter (company page's analyst tile)
  const [sector, setSector]     = useState('');
  const [industry, setIndustry] = useState('');

  useEffect(() => {
    api.get('/analysts/ratings')
      .then(r => setRows(r.data || []))
      .catch(e => setError(e.response?.data?.error || 'Failed to load analyst ratings'))
      .finally(() => setLoading(false));
  }, []);

  const sectors    = useMemo(() => [...new Set(rows.map(r => r.sector).filter(Boolean))].sort(), [rows]);
  const industries = useMemo(() => [...new Set(rows.map(r => r.industry).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter(r =>
      (!sector   || r.sector   === sector) &&
      (!industry || r.industry === industry) &&
      (!t || (r.ticker || '').toLowerCase().includes(t) ||
             (r.name || '').toLowerCase().includes(t) ||
             (r.sector || '').toLowerCase().includes(t) ||
             (r.industry || '').toLowerCase().includes(t) ||
             (r.analyst || '').toLowerCase().includes(t) ||
             (r.firm || '').toLowerCase().includes(t)));
  }, [rows, q, sector, industry]);

  return (
    <div style={S.page}>
      <Navbar backTo={{ label: 'Dashboard', href: '/dashboard' }} />
      <div style={S.wrap}>
        <div style={S.inner}>
          <div style={S.toolbar}>
            <input style={S.input} placeholder="Filter ticker, company, sector, analyst..." value={q} onChange={e => setQ(e.target.value)} />
            <select style={S.select} value={sector} onChange={e => setSector(e.target.value)}>
              <option value="">All sectors</option>
              {sectors.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select style={S.select} value={industry} onChange={e => setIndustry(e.target.value)}>
              <option value="">All industries</option>
              {industries.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} ratings</span>
          </div>
          {loading ? <div style={S.msg}>Loading...</div>
            : error ? <div style={{ ...S.msg, color: 'var(--error)' }}>{error}</div>
            : filtered.length === 0 ? <div style={S.msg}>No ratings found</div>
            : <AnalystTable rows={filtered} cols={COLS} defaultSort={{ key: 'date', dir: 'desc' }} />}
        </div>
      </div>
    </div>
  );
}
